import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export function useAdminAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const checkAdmin = async (email: string | undefined) => {
      if (!email) {
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }
      try {
        const { data: isAdmin } = await supabase.rpc('is_admin', { user_email: email });
        if (cancelled) return;
        setIsAuthenticated(!!isAdmin);
        if (isAdmin) setAdminEmail(email);
      } catch {
        if (cancelled) return;
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      checkAdmin(session?.user?.email);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setIsChecking(true);
      checkAdmin(session?.user?.email);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    // First check if email is an admin before attempting login
    try {
      const { data: isAdmin } = await supabase.rpc('is_admin', { user_email: email });
      if (!isAdmin) {
        return { success: false, error: '该邮箱无管理员权限' };
      }
    } catch {
      return { success: false, error: '权限校验失败，请稍后重试' };
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setAdminEmail(null);
  }, []);

  return { isAuthenticated, isChecking, login, logout, adminEmail };
}